import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import nurseIcon from '../../assets/Images/woman.png';
import { Prod01 } from '../../constants/ActionType';
import DateRangePicker, { DateRangeSummary } from '../common/DateRangePicker';
import { defaultRange } from '../../utils/dateRange';

// Card color comes from the server rule (backend config/nurseTargets.js): minutes vs the monthly target pro-rated
// to the selected range. Green >= greenPct, yellow >= yellowPct, red below. Existing card classes are reused.
export const COLOR_CLASS = { green: 'justPerformingCard', yellow: 'activePerformanceCard', red: 'underPerformingCard' };

export const NurseCardLegend = ({ rule }) => {
    if (!rule) return null;
    const sw = (bg) => ({ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: bg, verticalAlign: 'middle', marginRight: 6 });
    return (
        <div className="mt-3 p-2" style={{ border: '1px solid #e5e9f0', borderRadius: 8, fontSize: 14 }} data-testid="nurse-card-legend">
            <b>Card colors</b> — target for this range: <b>{rule.target} min</b> ({rule.monthlyTarget} min per month, pro-rated by days).
            <div className="mt-1">
                <span className="mr-3"><span style={sw('#008000')} />Green: at least {rule.greenPct}% of target</span>
                &nbsp;&nbsp;
                <span className="mr-3"><span style={sw('gold')} />Yellow: {rule.yellowPct}%–{rule.greenPct - 1}% of target</span>
                &nbsp;&nbsp;
                <span><span style={sw('#ff0000')} />Red: below {rule.yellowPct}% of target</span>
            </div>
            <small className="text-muted">Click a nurse card to open her activity for the same dates.</small>
        </div>
    );
};

// Projected / earned bonus on the cards: Super Admin + Org Manager only (server also enforces this).
export const BONUS_CARD_ROLES = ['admin', 'adminStaff'];

const NurseProgressComponent = () => {
    const history = useHistory();
    const { user } = useSelector((state) => state.auth);
    const withBonus = BONUS_CARD_ROLES.includes(user?.role);
    const [range, setRange] = useState(defaultRange());
    const [consentCategory, setConsentCategory] = useState('CCM');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async (r, program) => {
        setLoading(true);
        setError('');
        try {
            const { data: d } = await axios.get(`${Prod01}/nurse-activity/summary`, { params: { startDate: r.startDate, endDate: r.endDate, program, ...(withBonus ? { includeBonus: 1 } : {}) } });
            setData(d);
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not load nurse analytics');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [withBonus]);

    useEffect(() => {
        load(range, consentCategory);
    }, [load, range, consentCategory]);

    const openNurse = (n) => history.push(`/nurse-activity/${n._id}?startDate=${data.startDate}&endDate=${data.endDate}`);

    return (
        <div>
            <div className="row-display" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
                <DateRangePicker
                    id="nurse-analytics-range"
                    label="Date range"
                    startDate={range.startDate}
                    endDate={range.endDate}
                    onChange={({ startDate, endDate }) => setRange({ startDate, endDate })}
                />
                <div className="form-label">
                    <label className="form-label" htmlFor="nurse-analytics-category">Consent Type: </label>
                    <select id="nurse-analytics-category" className="form-control" name="category" value={consentCategory} onChange={(e) => setConsentCategory(e.target.value)}>
                        <option value="RPM">RPM Category</option>
                        <option value="CCM">CCM Category</option>
                    </select>
                </div>
                <div>
                    <button className="edit-pill" onClick={() => load(range, consentCategory)}>Search</button>
                </div>
            </div>

            <DateRangeSummary startDate={range.startDate} endDate={range.endDate} prefix={`${consentCategory} minutes added by nurses`} />
            {error && <div className="alert alert-danger py-2">{error}</div>}
            {loading && <small className="text-muted">Loading…</small>}

            <div className="row mt-2">
                {data && data.nurses.length > 0 ? (
                    data.nurses.map((n) => (
                        <div className="col-md-4 text-center" key={n._id}>
                            <button
                                type="button"
                                className={`card p-3 performanceCard w-100 ${COLOR_CLASS[n.color] || 'underPerformingCard'}`}
                                style={{ cursor: 'pointer', border: 0, height: 'auto', minHeight: 270 }}
                                onClick={() => openNurse(n)}
                                aria-label={`Open Nurse Activity for ${n.firstname} ${n.lastname}: ${n.minutes} minutes, ${n.color}`}
                                data-color={n.color}
                            >
                                <img src={nurseIcon} alt="" width="90" height="90" className="roundedBorderImg centeredImg" />
                                <br />
                                <small><b>{n.firstname} {n.lastname}</b></small>
                                {n.block === false ? <p><small style={{ color: 'white' }}>Active</small></p> : <p><small style={{ color: 'red' }}>Deactivated</small></p>}
                                <small>
                                    <span className="text-white">Total Mins Added</span> <br />
                                    <h3>{n.minutes}</h3>
                                    {n.pctOfTarget !== null && <span>{n.pctOfTarget}% of {data.rule.target}-min target</span>}
                                </small>
                                {n.bonus && (
                                    <small className="d-block mt-1" data-testid="card-bonus">
                                        <span className="text-white">{n.bonus.status === 'final' ? 'Earned bonus' : 'Projected bonus'}</span>{' '}
                                        <b>${(Number(n.bonus.total) || 0).toFixed(2)}</b>
                                        {n.bonus.status === 'projected' && <><br /><span style={{ fontSize: 11 }}>{n.bonus.label}</span></>}
                                    </small>
                                )}
                            </button>
                        </div>
                    ))
                ) : (
                    !loading && (
                        <div className="col-md-12">
                            <small style={{ color: 'red' }}>No Minutes added b/w this time span.</small>
                        </div>
                    )
                )}
            </div>
            <NurseCardLegend rule={data && data.rule} />
        </div>
    );
};

export default NurseProgressComponent;
